import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { IonLabel, IonSpinner } from '@ionic/react'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import isNil from 'lodash/isNil'
import omit from 'lodash/omit'
import omitBy from 'lodash/omitBy'

import { AvatarInput, useAvatarInput } from '@components/atoms/Avatar'
import { Button } from '@components/atoms/Button'
import { InputField } from '@components/atoms/InputField'
import { RequiredMark } from '@components/atoms/RequiredMark'
import { Title } from '@components/atoms/Title'
import { Login } from '@components/templates/login'

import { checkUsernameUniqueness, updateProfile } from '@services/updateProfile'
import { errorBoundary } from '@/services/errorBoundary'
import { UserInfo } from '@/services/user'
import { queryClient } from '@lib/queryClient'
import { useGtm } from '@/lib/hooks/useGtm'
import { useUserInfo } from '@/lib/hooks/useUserInfo'
import {
  UserInterestsStatus,
  useUserInterestsStatus,
} from '@/lib/hooks/useUserInterestsStatus'
import {
  amplitudeIdentifyEvent,
  amplitudeSetUserId,
  IIdentifyEvent,
  Tracking,
  TrackingEvent,
} from '@/lib/tracking'
import { getStreamSetUserInfo } from '@/lib/tracking/platform/getstream'
import setFormErrorMessages from '@utils/setFormErrorMessages'
import { multiplePeriodsPattern, usernamePattern } from '@utils/validation'
import { getPlatform } from '@/utils/getPlatform'
import { getPreferences } from '@/utils/preferences'
import { authType } from '@/utils/utils'
import { validateNameWithoutBlacklistedWords } from '@/utils/validateNameWithoutBlacklistedWords'
import { formatISOString } from 'utils/date'

import { BranchDataParams } from '@/enums'

const CreateProfile = () => {
  const { trackEvent } = useGtm()
  const { data: userInfo } = useUserInfo()
  const { t } = useTranslation('common')
  const { setUserInterestsStatus } = useUserInterestsStatus()
  const debounceTimer = useRef<NodeJS.Timeout>(null)
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setError,
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      username: '',
      displayName: '',
      avatar: '',
    },
    mode: 'onSubmit',
  })

  const isValid = Object.keys(errors).length === 0

  const isUsernameUnique = useCallback(async (username: string) => {
    // If the user name contains rpk or republik, there is no need to call the interface.
    if (!validateNameWithoutBlacklistedWords(username)) return false

    try {
      const result = await checkUsernameUniqueness(username)

      return !result.usernames.includes(username.toLowerCase())
    } catch (e) {
      console.log(e)
    }
  }, [])

  const isUsernamePattern = useCallback(
    (value: string) =>
      usernamePattern.test(value) && !multiplePeriodsPattern.test(value),
    []
  )

  const { url, takePicture, uploading, avatarError } = useAvatarInput()

  useEffect(() => {
    if (!url) return

    setValue('avatar', url)
    trigger('avatar')
  }, [url, setValue, trigger])

  const { mutate, isPending } = useMutation({
    mutationKey: ['updateProfile'],
    mutationFn: updateProfile,
    onSuccess: async (data: UserInfo) => {
      trackEvent({
        event: TrackingEvent.SignUp,
        value: {
          authType: authType(Boolean(data.email)),
          userId: data.id,
        },
      })
      setUserInterestsStatus(UserInterestsStatus.InProgress)
      queryClient.setQueryData(['user'], data)
      Tracking.triggerEvent(TrackingEvent.ProfileCreated, {
        timestamp: formatISOString(data.createdAt),
      })
      amplitudeSetUserId(data.id)
      getStreamSetUserInfo(data.id, data.username)
      getPreferences(BranchDataParams.RefUsername).then((localRefUsername) => {
        amplitudeIdentifyEvent<IIdentifyEvent>({
          userId: data.id,
          followerCount: data.followerCount,
          followingCount: data.followingCount,
          userName: data.username,
          refUserName: data.refUsername || localRefUsername || '-',
          isAmbassador: userInfo?.isAmbassador,
          badge: userInfo?.badge || '-',
          appPlatform: getPlatform(),
        })
      })
      Tracking.triggerEvent(TrackingEvent.BranchSignUp)
    },
    onError: (error: AxiosError) => {
      errorBoundary(error)
      setFormErrorMessages({ error, setError, t })
    },
  })

  const onSubmit = useCallback(
    async (data) => {
      mutate({
        userData: omitBy(omit(data, ['avatar']), isNil),
      })
    },
    [mutate]
  )

  const validationMessages = useMemo(
    () => ({
      required: t('required'),
      minLength: t('minLength', { length: 2 }),
      maxLength: t('maxLength', { length: 30 }),
      isUsernamePattern: t('pattern'),
      isUsernameUnique: t('usernameIsTaken'),
      validateNameWithoutBlacklistedWords: t('nameWithoutBlacklistedWords'),
    }),
    [t]
  )

  const title =
    !isDirty || !isValid ? (
      <Trans i18nKey="registration.profile.title">
        {t('registration.profile.title')} 🚀
      </Trans>
    ) : (
      <Trans i18nKey="registration.profile.titleValid">
        {t('registration.profile.titleValid')} ✨
      </Trans>
    )

  const handleUsernameChange = useCallback(
    (
        field: ControllerRenderProps<
          {
            username: string
            displayName: string
            avatar: string
          },
          'username'
        >
      ) =>
      async (event: { value: string }) => {
        clearTimeout(debounceTimer.current)
        field.onChange(event.value)
        const isValid = await trigger('username')

        if (!isValid) return

        debounceTimer.current = setTimeout(async () => {
          const isUnique = await isUsernameUnique(event.value)
          if (!isUnique) {
            setError('username', {
              message: validationMessages['isUsernameUnique'],
            })
          }
        }, 500)
      },
    [isUsernameUnique, setError, trigger, validationMessages]
  )

  return (
    <Login toolbarClassName="h-0">
      <Title className="mb-24" level={1}>
        {title}
      </Title>
      <form
        className="px-14 py-14 bg-[#1F2A37] rounded-t-3xl relative -mx-6"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="mx-auto absolute w-[8rem] h-[8rem] top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer">
          <Controller
            control={control}
            name="avatar"
            rules={{
              required: true,
            }}
            render={() => <></>}
          />
          <AvatarInput
            url={url}
            onClick={takePicture}
            uploading={uploading}
            hasIcon
            hasText
            isValid={!errors?.avatar}
          />
        </div>
        {avatarError && (
          <IonLabel color="danger" className="text-center block mt-4">
            {avatarError}
          </IonLabel>
        )}
        <div className="mt-9 mb-11 relative">
          <Controller
            control={control}
            name="username"
            rules={{
              minLength: 2,
              maxLength: 30,
              required: true,
              validate: {
                isUsernamePattern,
              },
            }}
            render={({ field }) => (
              <InputField
                label={t('registration.profile.userName')}
                labelSuffix={
                  <RequiredMark
                    style={{
                      top: '0.2rem',
                      right: '-0.2rem',
                      fontWeight: '900',
                    }}
                  />
                }
                value={field.value}
                isValid={!errors?.username}
                onChange={handleUsernameChange(field)}
              />
            )}
          />
          {errors?.username && (
            <IonLabel
              color="danger"
              className="mt-1 absolute top-full left-0 right-0"
            >
              {validationMessages[errors?.username?.type] ??
                errors?.username?.message}
            </IonLabel>
          )}
        </div>
        <div className="mb-11 relative">
          <Controller
            control={control}
            rules={{
              maxLength: 30,
              minLength: 2,
              validate: {
                validateNameWithoutBlacklistedWords,
              },
            }}
            name="displayName"
            render={({ field }) => (
              <InputField
                isValid={!errors?.displayName}
                label={t('registration.profile.displayName')}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                autocomplete="on"
              />
            )}
          />
          {errors?.displayName && (
            <IonLabel
              color="danger"
              className="mt-1 absolute top-full left-0 right-0"
            >
              {validationMessages[errors?.displayName?.type] ??
                errors?.displayName?.message}
            </IonLabel>
          )}
        </div>
        <Button
          size="large"
          className="mt-10 flex mx-auto w-lg"
          onClick={handleSubmit(onSubmit)}
        >
          {isPending ? (
            <IonSpinner color="light" />
          ) : (
            t('registration.profile.create')
          )}
        </Button>
      </form>
    </Login>
  )
}

export default CreateProfile
